/* ============================================
   useSupabase Hook — Generic CRUD Operations
   Drop-in replacement for the old Database class (app/db.js)
   Same interface: getAll, getById, add, update, delete, count
   ============================================ */

import { supabase } from '@/lib/supabase'

/**
 * Valid table names for the cotizador schema.
 * Using string type directly to avoid Supabase's strict generic
 * table discrimination which prevents dynamic table access.
 */
type CrudTable = 'empresa' | 'servicios' | 'clientes' | 'cotizaciones' | 'perfiles_equipo'

/**
 * Generic CRUD hook that mirrors the old db.js interface.
 * All page-*.js files used db.getAll('tabla'), db.getById('tabla', id), etc.
 * This hook provides the same API shape via Supabase.
 *
 * NOTE: We use the untyped supabase.from() pattern here intentionally.
 * The typed Database generic on createClient provides autocompletion
 * but the dynamic table name pattern requires runtime flexibility.
 * Type safety is enforced at the page/component level via explicit
 * generic parameters: getAll<Cliente>('clientes')
 */
export function useSupabase() {
  // Use the raw REST client to bypass strict table type checking
  // This is the documented pattern for dynamic table access in Supabase
  const client = supabase

  async function getAll<T>(table: CrudTable): Promise<T[]> {
    const { data, error } = await client
      .from(table)
      .select('*')
    if (error) throw error
    return (data ?? []) as T[]
  }

  async function getById<T>(table: CrudTable, id: number): Promise<T | null> {
    const { data, error } = await client
      .from(table)
      .select('*')
      .eq('id', id)
      .single()
    if (error) return null
    return data as T
  }

  async function add(
    table: CrudTable,
    record: Record<string, unknown>
  ): Promise<number> {
    // Remove id before insert — let Supabase auto-generate it
    const cleaned = { ...record }
    delete cleaned.id

    const { data, error } = await client
      .from(table)
      .insert(cleaned as never)
      .select('id')
      .single()
    if (error) throw error
    return (data as unknown as { id: number }).id
  }

  async function update(
    table: CrudTable,
    record: Record<string, unknown>
  ): Promise<boolean> {
    const id = record.id as number
    const cleaned = { ...record }
    delete cleaned.id

    const { error } = await client
      .from(table)
      .update(cleaned as never)
      .eq('id' as never, id as never)
    if (error) throw error
    return true
  }

  async function remove(table: CrudTable, id: number): Promise<boolean> {
    const { error } = await client
      .from(table)
      .delete()
      .eq('id' as never, id as never)
    if (error) throw error
    return true
  }

  async function count(table: CrudTable): Promise<number> {
    const { count: total, error } = await client
      .from(table)
      .select('*', { count: 'exact', head: true })
    if (error) throw error
    return total ?? 0
  }

  return { getAll, getById, add, update, remove, count }
}
